import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiUpload, FiX, FiSave, FiArrowLeft } from 'react-icons/fi';
import { extensionsAPI } from '../services/api';
import './CreateExtension.css';

const CreateExtension = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    version: '1.0.0',
    icon: null,
    file: null,
    isWhitelabeled: false,
    isUpgradeable: true,
    repoMention: '',
    chromeWebStoreLink: ''
  });
  const [loading, setLoading] = useState(false);

  const categories = [
    'Social & Communication',
    'Productivity',
    'Entertainment',
    'Shopping',
    'News & Weather',
    'Developer Tools',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'icon' && !file.type.startsWith('image/')) {
        toast.error('Please select a valid image file for the icon');
        return;
      }
      if (type === 'file' && !file.name.endsWith('.zip')) {
        toast.error('Please select a valid ZIP file for the extension');
        return;
      }
      setFormData(prev => ({
        ...prev,
        [type]: file
      }));
    }
  };

  const removeFile = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: null
    }));
  };

  // Convert file to base64 data URL
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare extension data
      const extensionData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        version: formData.version,
        isWhitelabeled: formData.isWhitelabeled,
        isUpgradeable: formData.isUpgradeable,
        repoMention: formData.repoMention,
        chromeWebStoreLink: formData.chromeWebStoreLink,
        plans: [], // Default empty plans array
        status: 'active' // Default status
      };

      // Convert icon to base64 if provided
      if (formData.icon) {
        try {
          const iconBase64 = await fileToBase64(formData.icon);
          extensionData.icon = iconBase64;
        } catch (error) {
          console.error('Error converting icon to base64:', error);
          // Fallback to placeholder
          extensionData.icon = 'https://via.placeholder.com/60x60/039BE5/FFFFFF?text=' + formData.name.charAt(0).toUpperCase();
        }
      } else {
        // Use placeholder if no icon provided
        extensionData.icon = 'https://via.placeholder.com/60x60/039BE5/FFFFFF?text=' + formData.name.charAt(0).toUpperCase();
      }

      // Convert file to base64 if provided
      if (formData.file) {
        try {
          const fileBase64 = await fileToBase64(formData.file);
          extensionData.fileUrl = fileBase64;
          extensionData.fileName = formData.file.name;
        } catch (error) {
          console.error('Error converting file to base64:', error);
          toast.error('Error processing extension file');
          return;
        }
      }

      // Create extension via API
      const response = await extensionsAPI.create(extensionData);
      
      if (response.success) {
        toast.success('Extension created successfully!');
        navigate('/dashboard');
      } else {
        toast.error(response.message || 'Failed to create extension');
      }
    } catch (error) {
      console.error('Failed to create extension:', error);
      toast.error(error.message || 'Failed to create extension');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-extension-page">
      <div className="headerSection">
        <div className="page-header">
          <div className="page-title">
            <h2>Create New Extension</h2>
            <p>Configure your browser extension (file upload is optional)</p>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <FiArrowLeft />
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="dataArea-content">
        <div className="create-form-container">
          <form onSubmit={handleSubmit} className="create-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Extension Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter extension name"
                  required
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="form-control"
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your extension..."
                rows="4"
                className="form-control"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="version">Version</label>
                <input
                  type="text"
                  id="version"
                  name="version"
                  value={formData.version}
                  onChange={handleChange}
                  placeholder="1.0.0"
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="repoMention">Repository Mention</label>
                <input
                  type="text"
                  id="repoMention"
                  name="repoMention"
                  value={formData.repoMention}
                  onChange={handleChange}
                  placeholder="e.g., Feather Connector Whitelabel (V2.0.0)"
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="chromeWebStoreLink">Chrome Web Store Link</label>
                <input
                  type="url"
                  id="chromeWebStoreLink"
                  name="chromeWebStoreLink"
                  value={formData.chromeWebStoreLink}
                  onChange={handleChange}
                  placeholder="https://chrome.google.com/webstore/detail/..."
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isWhitelabeled"
                    checked={formData.isWhitelabeled}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkmark"></span>
                  Whitelabeled
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isUpgradeable"
                    checked={formData.isUpgradeable}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkmark"></span>
                  Upgradeable
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="icon">Extension Icon (60x60)</label>
                <div className="file-upload">
                  {formData.icon ? (
                    <div className="file-preview">
                      <img src={URL.createObjectURL(formData.icon)} alt="Icon preview" />
                      <button
                        type="button"
                        onClick={() => removeFile('icon')}
                        className="remove-file"
                      >
                        <FiX />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-area">
                      <FiUpload />
                      <span>Click to upload icon (60x60 PNG, JPG)</span>
                      <input
                        type="file"
                        id="icon"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'icon')}
                        className="file-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="file">Extension File (Optional)</label>
                <div className="file-upload">
                  {formData.file ? (
                    <div className="file-preview">
                      <div className="file-info">
                        <span className="file-name">{formData.file.name}</span>
                        <span className="file-size">
                          {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile('file')}
                        className="remove-file"
                      >
                        <FiX />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-area">
                      <FiUpload />
                      <span>Click to upload extension (ZIP) - Optional</span>
                      <input
                        type="file"
                        id="file"
                        accept=".zip"
                        onChange={(e) => handleFileChange(e, 'file')}
                        className="file-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn btn-hollow"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.name || !formData.category}
                className="btn btn-blue"
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <FiSave />
                    Create Extension
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateExtension; 